import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Video, Loader } from 'lucide-react';

// ---------------------------------------------------------------------------
// Packing capture — "scan the packing slip → record the packing" flow.
//
// The packer opens the Packing Invoice, scans the order barcode to start, packs
// the box, then scans again to stop. On stop we export the REAL recorded clip
// for that window from the DW Spectrum NVR archive and store it as a durable
// file in R2 (Neon row tracks it). The footage is then viewable any time from
// the "View Packing" pill on the order — it persists across sessions.
//
//   start scan / stop scan   -> capture [startMs, endMs]
//   dw-clip-export-background -> pull /media/<cam>.mp4 for the window -> R2
//   packing-clip (status|url) -> the order page polls + plays the stored file
//
// While recording we also show a live snapshot preview (the DW proxy's still
// endpoint) so the packer can see the line; the saved artifact is the real clip.
// ---------------------------------------------------------------------------

// Z10 Packing Lines 2 & 3 — id from the Cameras page (DW Spectrum device id).
const Z10_CAMERA_ID = '{caef70f1-8863-3ad4-814e-9b7a2d72d6b9}';
const Z10_CAMERA_NAME = 'Z10 Packing Lines 2 & 3';

const FN_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions';

function post(fn, body) {
  return fetch(`${FN_BASE}/${fn}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

// Latest packing recording row for an order (or null). Used by the pill + modal.
export async function fetchPackingStatus(bcOrderId) {
  try {
    const r = await post('packing-clip', { action: 'status', bcOrderId: String(bcOrderId) });
    const d = await r.json();
    return d.row || null;
  } catch { return null; }
}

async function fetchPackingUrl(bcOrderId) {
  const r = await post('packing-clip', { action: 'url', bcOrderId: String(bcOrderId) });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || 'No playback URL');
  return d;
}

// Inject the Code 39 barcode font + keyframes once.
function usePackingAssets() {
  useEffect(() => {
    if (!document.getElementById('libre-barcode-39-font')) {
      const link = document.createElement('link');
      link.id = 'libre-barcode-39-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Libre+Barcode+39+Text&display=swap';
      document.head.appendChild(link);
    }
    if (!document.getElementById('packing-capture-style')) {
      const style = document.createElement('style');
      style.id = 'packing-capture-style';
      style.textContent = `
        @keyframes packBlink { 0%,100%{opacity:1} 50%{opacity:0.25} }
        @keyframes packScanGlow { 0%,100%{box-shadow:0 0 0 0 rgba(220,88,42,0)} 50%{box-shadow:0 0 0 4px rgba(220,88,42,0.25)} }
        @keyframes packSpin { to { transform: rotate(360deg) } }
      `;
      document.head.appendChild(style);
    }
  }, []);
}

function fmtClock(ms) {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
function fmtTime(ms) {
  if (!ms) return '';
  const d = new Date(Number(ms));
  const h = d.getHours() % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')} ${d.getHours() >= 12 ? 'PM' : 'AM'}`;
}

// ===========================================================================
// Packing Invoice modal — barcode + real clip capture
// ===========================================================================
export function PackingInvoiceModal({ order, shippingInstructions, initialStatus, onClose, onReady }) {
  usePackingAssets();
  const bcId = String(order.id);

  // phase: idle | recording | exporting | ready | error
  const initialReady = initialStatus && initialStatus.status === 'ready';
  const [phase, setPhase] = useState(initialReady ? 'ready' : 'idle');
  const [elapsed, setElapsed] = useState(0);
  const [errMsg, setErrMsg] = useState(initialStatus && initialStatus.status === 'error' ? initialStatus.error : null);
  const [savedRow, setSavedRow] = useState(initialReady ? initialStatus : null);

  const startRef = useRef(null);
  const tickRef = useRef(null);
  const pollRef = useRef(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const clearAll = useCallback(() => {
    [tickRef, pollRef].forEach(r => { if (r.current) { clearInterval(r.current); r.current = null; } });
  }, []);
  useEffect(() => () => clearAll(), [clearAll]);

  const startRecording = useCallback(() => {
    startRef.current = Date.now();
    setElapsed(0);
    setErrMsg(null);
    setPhase('recording');
    tickRef.current = setInterval(() => setElapsed(Date.now() - startRef.current), 250);
  }, []);

  const stopRecording = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    const startMs = startRef.current;
    const endMs = Date.now();
    setPhase('exporting');

    post('dw-clip-export-background', {
      bcOrderId: bcId,
      internalOrderId: order.internalId ? String(order.internalId) : null,
      cameraId: Z10_CAMERA_ID,
      cameraName: Z10_CAMERA_NAME,
      startMs, endMs,
    }).catch(() => {});

    // Poll for the export to finish (background job writes status to Neon).
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      const row = await fetchPackingStatus(bcId);
      if (row && row.status === 'ready') {
        clearInterval(pollRef.current); pollRef.current = null;
        setSavedRow(row); setPhase('ready');
        if (onReady) onReady(row);
      } else if (row && row.status === 'error') {
        clearInterval(pollRef.current); pollRef.current = null;
        setErrMsg(row.error || 'Export failed'); setPhase('error');
      } else if (tries > 45) { // ~2.5 min ceiling
        clearInterval(pollRef.current); pollRef.current = null;
        setErrMsg('Timed out waiting for the clip to export.'); setPhase('error');
      }
    }, 3000);
  }, [bcId, order.internalId, onReady]);

  const handleScan = useCallback(() => {
    const p = phaseRef.current;
    if (p === 'recording') stopRecording();
    else if (p === 'exporting') { /* busy — ignore */ }
    else startRecording();
  }, [startRecording, stopRecording]);

  // Handheld scanner = keyboard wedge: types the barcode fast, ends with Enter.
  useEffect(() => {
    let buf = '', last = 0;
    function onKey(e) {
      const now = Date.now();
      if (now - last > 120) buf = '';
      last = now;
      if (e.key === 'Enter') {
        const v = buf.replace(/[^0-9]/g, '');
        if (v && (v === bcId || v.endsWith(bcId))) { e.preventDefault(); handleScan(); }
        buf = '';
      } else if (e.key.length === 1) buf += e.key;
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [bcId, handleScan]);

  const billing = order.customer?.billing || {};
  const shipping = order.customer?.shipping || {};
  const recording = phase === 'recording';
  const busy = phase === 'recording' || phase === 'exporting';

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflow: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, padding: '24px 28px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: 'var(--navy)', fontSize: 22, fontWeight: 800 }}>Packing Invoice</h3>
          <button onClick={onClose} disabled={busy} title={busy ? 'Finish capture first' : 'Close'}
            style={{ background: 'none', border: 'none', cursor: busy ? 'not-allowed' : 'pointer', color: 'var(--gray-400)', opacity: busy ? 0.4 : 1 }}>
            <X size={20} />
          </button>
        </div>

        {/* Barcode — the BC order number (starts with 2). Click or scan it. */}
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div
            onClick={handleScan}
            title="Scan to start/stop packing capture (or scan with a handheld scanner)"
            style={{
              display: 'inline-block', cursor: busy && !recording ? 'default' : 'pointer', padding: '4px 18px', borderRadius: 8,
              border: '2px solid transparent', userSelect: 'none',
              animation: recording ? 'packScanGlow 1.2s infinite' : 'none', transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => { if (!busy || recording) e.currentTarget.style.borderColor = 'var(--orange)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
          >
            <div style={{ fontFamily: "'Libre Barcode 39 Text', monospace", fontSize: 72, lineHeight: 1, color: '#111' }}>
              *{bcId}*
            </div>
          </div>
        </div>

        {/* Order meta */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 13, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--gray-200)' }}>
          <div><strong>Date:</strong> {order.orderDate || '—'}</div>
          <div><strong>Delivery Date:</strong> {order.selectedDelivery || order.estimatedDelivery || '—'}</div>
          <div><strong>Order ID:</strong> {bcId}</div>
          {order.trackingNumber ? <div><strong>Tracking:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{order.trackingNumber}</span></div> : <div />}
        </div>

        {/* Bill To / Ship To */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, fontSize: 13, marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Bill To:</div>
            <div>{billing.name}</div>
            <div>{billing.address}</div>
            <div>{billing.city}{billing.city ? ',' : ''} {billing.state} {billing.zip}</div>
            {billing.phone && <div><strong>Day Phone:</strong> {billing.phone}</div>}
            {billing.email && <div style={{ color: 'var(--orange)' }}>{billing.email}</div>}
          </div>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Ship To:</div>
            <div>{shipping.name}</div>
            <div>{shipping.address}</div>
            <div>{shipping.city}{shipping.city ? ',' : ''} {shipping.state} {shipping.zip}</div>
            {shipping.phone && <div><strong>Day Phone:</strong> {shipping.phone}</div>}
          </div>
        </div>

        {/* Office Notes */}
        {shippingInstructions && (
          <div style={{ fontSize: 12, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--gray-200)' }}>
            <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Office Notes:</div>
            <div style={{ whiteSpace: 'pre-wrap', color: 'var(--gray-700)' }}>{shippingInstructions}</div>
          </div>
        )}

        {/* Products to pack */}
        {order.products?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Products</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {order.products.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '6px 8px', width: 44 }}>
                      {p.image ? <img src={p.image} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} /> : null}
                    </td>
                    <td style={{ padding: '6px 8px', fontWeight: 700, width: 40, color: 'var(--navy)' }}>{p.quantity}×</td>
                    <td style={{ padding: '6px 8px' }}>{p.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Packing capture control */}
        <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 16 }}>
          {recording && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10, color: '#dc2626', fontSize: 13, fontWeight: 700 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#dc2626', animation: 'packBlink 1s infinite' }} />
              Recording {fmtClock(elapsed)}
            </div>
          )}
          {phase === 'ready' && (
            <div style={{ textAlign: 'center', marginBottom: 10, color: '#16a34a', fontSize: 13, fontWeight: 700 }}>
              ✓ Packing footage saved{savedRow?.file_size ? ` · ${(savedRow.file_size / 1e6).toFixed(1)} MB` : ''} — view it from the “View Packing” pill
            </div>
          )}
          {phase === 'error' && (
            <div style={{ textAlign: 'center', marginBottom: 10, color: '#dc2626', fontSize: 12 }}>{errMsg}</div>
          )}
          <button
            onClick={handleScan}
            disabled={phase === 'exporting'}
            style={{
              width: '100%', fontSize: 15, fontWeight: 700, padding: '12px 16px', borderRadius: 8, border: 'none',
              cursor: phase === 'exporting' ? 'not-allowed' : 'pointer', color: '#fff',
              background: recording ? '#16a34a' : phase === 'exporting' ? '#94a3b8' : 'var(--orange)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {phase === 'exporting' && <Loader size={16} style={{ animation: 'packSpin 1s linear infinite' }} />}
            {recording ? 'Stop Scan' : phase === 'exporting' ? 'Saving…' : (phase === 'ready' || phase === 'error') ? 'Rescan' : 'Start Scan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// View Packing — playback overlay (plays the stored R2 clip)
// ===========================================================================
export function PackingVideoOverlay({ order, onClose }) {
  const [url, setUrl] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchPackingUrl(order.id)
      .then(d => { if (alive) { setUrl(d.url); setMeta(d); } })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [order.id]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#0b0e14', borderRadius: 12, width: '100%', maxWidth: 920, overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #1e2530' }}>
          <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Video size={16} /> Packing — Order #{order.id}
            <span style={{ color: '#64748b', fontWeight: 500, fontSize: 12 }}>{Z10_CAMERA_NAME}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
        </div>

        <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {url ? (
            <video src={url} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
          ) : err ? (
            <div style={{ color: '#fca5a5', fontSize: 14, textAlign: 'center', padding: 24 }}>{err}</div>
          ) : (
            <div style={{ color: '#475569', fontSize: 14, textAlign: 'center' }}>
              <Loader size={26} style={{ animation: 'packSpin 1s linear infinite', marginBottom: 8 }} /><br />Loading footage…
            </div>
          )}
        </div>

        {meta && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', fontSize: 11, color: '#64748b' }}>
            <span>Recorded {fmtTime(meta.startedAt)} – {fmtTime(meta.endedAt)}</span>
            <span>{meta.durationMs ? `${Math.round(meta.durationMs / 1000)}s` : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
}
